import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/GridLegacy';
import { DataGrid, GridFooter, GridToolbar } from '@mui/x-data-grid';
import Radio from '@mui/material/Radio';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Slide from '@mui/material/Slide';
import dayjs from 'dayjs';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQuery } from 'react-query';
import PropTypes from 'prop-types';
import PageLayout from '../shared/PageLayout/PageLayout';
import { formatPhone } from '../../scripts/phoneNumberHelper';
import CustomLoading from '../shared/CustomLoading';
import { QueryKey } from '../../data/queryHooks/QueryKeys';
import {
	GetProviders,
	GetClinicLocations,
	PutChangeClinic,
} from '../../data/apiCalls';
import AsyncAutocomplete from '../shared/AsyncAutocomplete/AsyncAutocomplete';
import CustomNoResultsOverlay from '../shared/CustomNoResultsOverlay';
import useSessionContext from '../../data/queryHooks/useSessionContext';
import { getHomePagePath } from '../../scripts/navigationHelpers';
import { HOME_URL } from '../../scripts/navigationItems';
import useIsMobile from '../../hooks/useIsMobile';

const SlideLeft = props => <Slide {...props} direction='left' />;

const CenteredCell = ({ children }) => (
	<Stack justifyContent='center' sx={{ height: '100%', py: 2 }}>
		{children}
	</Stack>
);

CenteredCell.propTypes = {
	children: PropTypes.node,
};

const SelectClinicLocation = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const [showErrorBanner, setShowErrorBanner] = useState(false);
	const renderConfirmLocation = location?.pathname?.includes(
		'confirmClinicLocation'
	);

	const {
		ashlinkUserId,
		ashlinkUserEntityType,
		practitionerId,
		practitionerNetCode,
		providerNetCode,
		permissions,
		redirectionLinks,
	} = useSessionContext();
	const isMobile = useIsMobile();

	// provider dropdown
	const [selectedProvider, setSelectedProvider] = useState(null);
	const dropdownPageSize = 20;
	const [dropdownPage, setDropdownPage] = useState(1);
	const [dropdownSearchString, setDropdownSearchString] = useState('');

	// clinic location table
	const [selectionModel, setSelectionModel] = useState('');
	const [paginationModel, setPaginationModel] = useState({
		page: 0,
		pageSize: 10,
	});
	const [searchString, setSearchString] = useState(undefined);

	const { isLoading: isLoadingProviderList, data: providerQueryData } =
		useQuery(
			[QueryKey.GET_PROVIDERS, dropdownPage, dropdownSearchString],
			() =>
				GetProviders(
					ashlinkUserId,
					ashlinkUserEntityType,
					dropdownSearchString,
					dropdownPage,
					dropdownPageSize
				),
			{
				staleTime: Infinity,
				refetchOnWindowFocus: false,
			}
		);

	const providerList =
		providerQueryData?.data?.data?.ashlinkUserEntities ?? [];
	const totalProviderRows = providerQueryData?.data?.data?.totalRows ?? 0;

	const updateDropdownQuery = (value, pageNum) => {
		setDropdownPage(pageNum);
		setDropdownSearchString(value);
	};

	// setFieldValue received from Formik
	const dropdownOnChange = setFieldValue => value => {
		setFieldValue('providerID', value?.providerId ?? '');
		setSelectedProvider(value);
	};

	const {
		isLoading,
		isRefetching,
		data: clinicLocationQueryData,
	} = useQuery(
		[
			QueryKey.GET_CLINIC_LOCATIONS,
			paginationModel.page,
			paginationModel.pageSize,
			searchString ?? '',
			selectedProvider?.providerId,
		],
		() =>
			GetClinicLocations(
				ashlinkUserId,
				selectedProvider?.providerId,
				ashlinkUserEntityType,
				providerNetCode,
				practitionerId,
				practitionerNetCode,
				searchString,
				paginationModel.page + 1,
				paginationModel.pageSize
			),
		{
			enabled: !!selectedProvider,
			staleTime: Infinity,
			refetchOnWindowFocus: false,
			keepPreviousData: true,
		}
	);

	const clinicLocations =
		clinicLocationQueryData?.data?.data?.ashlinkUserProfiles ?? [];
	const totalRows = clinicLocationQueryData?.data?.data?.totalRows;

	// Keep the last known rowCount so DataGrid never sees rowCount=0 during
	// a page change, which would cause it to reset pagination back to page 0.
	const [rowCountState, setRowCountState] = useState(0);
	useEffect(() => {
		setRowCountState(prev => (totalRows !== undefined ? totalRows : prev));
	}, [totalRows]);

	const { mutate, isLoading: isFetching } = useMutation({
		mutationFn: values => PutChangeClinic(values),
		onSuccess: () => {
			const homePath = getHomePagePath(permissions, redirectionLinks);

			if (!homePath || homePath?.includes('.aspx')) {
				window.open(homePath || HOME_URL, '_self', 'noreferrer');
			} else {
				navigate(homePath);
			}
		},
		onError: () => {
			setShowErrorBanner(true);
		},
	});

	const handleSubmit = values => {
		if (showErrorBanner) {
			setShowErrorBanner(false);
		}

		mutate({
			AshlinkUserId: ashlinkUserId,
			AshlinkUserEntityType: ashlinkUserEntityType,
			ProviderId: selectedProvider.providerId,
			ProfileId: values.ProfileID,
			SetDefault: values.setDefault,
			PractitionerId: practitionerId,
			PractitionerNetCode: practitionerNetCode,
			ProviderNetCode: providerNetCode,
		});
	};

	const validationSchema = Yup.object().shape({
		providerID: Yup.string().required(
			'Please select a Provider/Practitioner'
		),
		DrFedID: Yup.number().required('Please select a location'),
		setDefault: Yup.bool(),
	});

	const checkRowId = row => `${row.drFedID}${row.profileID}${row.taxID}`;

	const columns = [
		{
			field: 'selected',
			headerName: 'Select',
			sortable: false,
			flex: 1,
			minWidth: 75,
			renderCell: e => (
				<CenteredCell>
					<Radio
						checked={checkRowId(selectionModel) === e.id}
						value={e.id}
						inputProps={{
							'aria-label': `${e.row.clinicName} at ${e.row.address1}`,
						}}
					/>
				</CenteredCell>
			),
		},
		{
			field: 'clinicName',
			headerName: 'Clinics',
			flex: 2,
			minWidth: 300,
			renderCell: params => <CenteredCell>{params.value}</CenteredCell>,
		},
		{
			field: 'address',
			headerName: 'Address',
			sortable: false,
			flex: 4,
			minWidth: 300,
			renderCell: params => (
				<CenteredCell>
					<div>{params.row.address1}</div>
					<div>{params.row.address2}</div>
					<div>
						{params.row.city}, {params.row.state}{' '}
						{params.row.zipCode}
					</div>
				</CenteredCell>
			),
		},
		{
			field: 'info',
			headerName: 'Info',
			sortable: false,
			flex: 2,
			minWidth: 200,
			renderCell: params => (
				<CenteredCell>
					<span>
						<b>Phone: </b>
						{formatPhone(params.row.phone) || 'N/A'}
					</span>
					<span>
						<b>Fax: </b>
						{formatPhone(params.row.fax) || 'N/A'}
					</span>
					<span>
						<b>Tax ID#: </b>
						{params.row.taxID}
					</span>
				</CenteredCell>
			),
		},
		{
			field: 'drEffectiveDate',
			headerName: 'Effective',
			flex: 1,
			minWidth: 100,
			valueFormatter: value => {
				if (!value) return '';
				return dayjs(value).format('MM/DD/YYYY');
			},
			renderCell: params => (
				<CenteredCell>{params.formattedValue}</CenteredCell>
			),
		},
		{
			field: 'drTerminationDate',
			headerName: 'Termed',
			flex: 1,
			minWidth: 100,
			valueFormatter: value => {
				if (!value) return '';
				return dayjs(value).format('MM/DD/YYYY');
			},
			renderCell: params => (
				<CenteredCell>{params.formattedValue}</CenteredCell>
			),
		},
	];

	const renderOptionLabel = option => {
		let label = '';

		if (option?.lastName) label += option.lastName;
		if (option?.firstName) label += `, ${option.firstName}`;
		if (option?.npiType1) label += ` - ${option?.npiType1}`;
		if (option?.specialtyDescription)
			label += ` - ${option?.specialtyDescription}`;

		return label;
	};

	return (
		<PageLayout
			pageTitle={
				renderConfirmLocation ? 'CONFIRM YOUR CLINIC' : 'Switch Clinic'
			}
			renderBackButton={renderConfirmLocation}
		>
			<Paper elevation={0} role='main' sx={{ py: 3, px: 4 }}>
				<Formik
					initialValues={{
						providerID: '',
						DrFedID: '',
						setDefault: renderConfirmLocation,
						ProfileID: '',
					}}
					validationSchema={validationSchema}
					onSubmit={handleSubmit}
				>
					{({ setFieldValue, values, touched, errors }) => (
						<Form>
							<Grid container spacing={4}>
								<Grid item xs={12}>
									<Typography variant='body1'>
										{renderConfirmLocation ? (
											'Please select your default clinic. This will be the default clinic each time you login. You will be able to change this later.'
										) : (
											<b>
												To view information for a
												specific clinic, please select
												the clinic below
											</b>
										)}
									</Typography>
								</Grid>
								<Grid item xs={12}>
									<Typography
										variant='subtitle2'
										component='label'
										gutterBottom
										sx={{
											mt: 5,
											'.MuiDataGrid-toolbarContainer': {
												display: isMobile && 'block',
											},
										}}
										htmlFor='providerID'
									>
										1. Select Provider/Practitioner
									</Typography>
									<AsyncAutocomplete
										id='providerID'
										data-testid='providerID'
										options={providerList}
										selectedOption={selectedProvider}
										totalOptions={totalProviderRows}
										loading={isLoadingProviderList}
										onUpdateQuery={updateDropdownQuery}
										onChange={dropdownOnChange(
											setFieldValue
										)}
										componentsProps={{
											paper: {
												elevation: 4,
											},
											popper: {
												style: { width: 'fit-content' },
												placement: 'bottom-start',
											},
										}}
										getOptionLabel={option =>
											renderOptionLabel(option)
										}
										renderInputProps={{
											placeholder:
												'Select Provider/Practitioner',
											error:
												touched?.providerID &&
												Boolean(errors?.providerID),
											helperText:
												touched?.providerID &&
												errors?.providerID,
											fullWidth: false,
											sx: {
												minWidth: {
													xs: '100%',
													md: '50%',
												},
												maxWidth: '100%',
											},
										}}
										isOptionEqualToValue={(option, value) =>
											option.name === value.name
										}
									/>
								</Grid>

								<Grid item xs={12}>
									<Typography
										variant='subtitle2'
										component='label'
										gutterBottom
										sx={{
											display: 'block',
										}}
									>
										2. Select{' '}
										{renderConfirmLocation && 'Default '}
										Clinic
									</Typography>
									<DataGrid
										sx={{
											mt: 5,
											'.MuiDataGrid-toolbarContainer': {
												display: isMobile && 'block',
											},
										}}
										showToolbar
										autoHeight
										pagination
										rows={clinicLocations ?? []}
										rowCount={rowCountState}
										loading={isLoading || isRefetching}
										columns={columns}
										getRowHeight={() => 'auto'}
										getEstimatedRowHeight={() => 100}
										getRowId={row => checkRowId(row)}
										slots={{
											noRowsOverlay:
												CustomNoResultsOverlay,
											loadingOverlay: CustomLoading,
											footer: GridFooter,
											toolbar: GridToolbar,
										}}
										slotProps={{
											noRowsOverlay: {
												customFailureVerbiage:
													selectedProvider &&
													!isLoadingProviderList
														? 'No Locations Available'
														: 'Please select a provider to view available clinics',
											},
											toolbar: {
												showQuickFilter: true,
												printOptions: {
													disableToolbarButton: true,
												},
												csvOptions: {
													disableToolbarButton: true,
												},
												quickFilterProps: {
													debounceMs: 300,
												},
											},
										}}
										paginationModel={paginationModel}
										onPaginationModelChange={
											setPaginationModel
										}
										pageSizeOptions={[10, 20, 50]}
										hideFooterSelectedRowCount
										disableRowSelectionOnClick
										disableMultipleRowSelection
										disableColumnMenu
										disableColumnFilter
										disableColumnResize
										disableColumnSelector
										disableDensitySelector
										filterMode='server'
										paginationMode='server'
										onFilterModelChange={filter => {
											setSearchString(
												filter.quickFilterValues.join(
													' '
												)
											);
											setPaginationModel(prev => ({
												...prev,
												page: 0,
											}));
										}}
										onCellClick={p => {
											const newSelectionModel =
												clinicLocations.find(
													x => checkRowId(x) === p.id
												);

											if (newSelectionModel) {
												setSelectionModel(
													newSelectionModel
												);
												setFieldValue(
													'DrFedID',
													newSelectionModel.drFedID
												);
												setFieldValue(
													'ProfileID',
													newSelectionModel.profileID
												);
											}
										}}
										initialState={{
											sorting: {
												sortModel: [
													{
														field: 'clinicName',
														sort: 'asc',
													},
												],
											},
										}}
										sortingOrder={['asc', 'desc']}
									/>
									<FormHelperText
										error={
											touched.drFedID &&
											Boolean(errors.drFedID)
										}
									>
										{touched.drFedID && errors.drFedID}
									</FormHelperText>
								</Grid>
								<Grid item xs={12}>
									<Stack alignItems='center' spacing={5}>
										{!renderConfirmLocation && (
											<FormControlLabel
												control={
													<Checkbox
														checked={
															values.setDefault
														}
													/>
												}
												label='Set as Default Clinic for ASHLink'
												name='setDefault'
												onChange={event =>
													setFieldValue(
														'setDefault',
														event.target.checked
													)
												}
											/>
										)}
										<Button
											variant='contained'
											type='submit'
											disabled={isFetching}
										>
											{renderConfirmLocation
												? 'Confirm'
												: 'Switch Clinic'}

											{isFetching && (
												<CircularProgress
													size={24}
													sx={{
														position: 'absolute',
														top: '50%',
														left: '50%',
														marginTop: '-12px',
														marginLeft: '-12px',
													}}
												/>
											)}
										</Button>
									</Stack>
								</Grid>
							</Grid>
						</Form>
					)}
				</Formik>
			</Paper>
			<Snackbar
				open={showErrorBanner}
				autoHideDuration={10000}
				onClose={() => setShowErrorBanner(false)}
				anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
				TransitionComponent={SlideLeft}
				sx={{
					boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px',
				}}
			>
				<Alert severity='error'>
					<Typography fontWeight={700}>
						Sorry, something went wrong.
					</Typography>
					<Typography>
						We were unable to process your request.
					</Typography>
				</Alert>
			</Snackbar>
		</PageLayout>
	);
};

export default SelectClinicLocation;
